import styled from 'styled-components';
import {
  Page,
  Container,
  Section,
  Eyebrow,
  Display,
  SectionTitle,
  Lead,
  Card,
  Grid,
  Facts,
  PrimaryButton,
  GhostButton,
  Check,
  CheckList,
  DISCORD_DM,
  DISCORD_SERVER,
} from '../Layout/PageLayout';
import { DiscordIcon } from '../Icons/CustomIcons';

const BRIEF = [
  ['The game', 'The genre and a link, if the game is public.'],
  ['The problem', 'What needs to be built or fixed.'],
  ['Current state', 'The existing code and tools, and the size of the team.'],
  ['Deadline & budget', 'Your deadline and an approximate budget.'],
];

const FIT = [
  [
    'When to contact me',
    [
      'You need backend architecture or a redesign of an existing system',
      'A live game needs optimization or a cleanup of old code',
    ],
  ],
  [
    'Best-fit work',
    [
      'Data persistence and networking',
      'Performance fixes and support for live games',
    ],
  ],
];

const Brief = styled(Card)`
  flex: 1 1 26.25rem;
  min-width: 0;
  align-self: flex-start;
  border-radius: 0.75rem;

  header {
    display: flex;
    justify-content: space-between;
    padding: 0.875rem 1.5rem;
    border-bottom: 1px solid var(--border);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }

  ol {
    list-style: none;
    padding: 0.75rem 1.5rem 1.5rem;
  }

  li {
    display: flex;
    gap: 1.25rem;
    padding: 1.125rem 0;
    border-bottom: 1px solid var(--border);
  }

  li:last-child {
    border-bottom: 0;
  }

  li > span {
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    color: var(--brand);
    padding-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: 0.25rem;
  }

  p {
    font-size: 0.9375rem;
  }
`;

const FitCard = styled(Card)`
  padding: 2rem;

  h3 {
    font-size: 1.375rem;
    margin-bottom: 1.25rem;
  }

  ${CheckList} {
    color: var(--text-code);
    font-size: 1rem;
  }
`;

const Contact = () => (
  <Page>
    <Section style={{ paddingTop: '6rem' }}>
      <Container style={{ display: 'flex', flexWrap: 'wrap', gap: '3.5rem 4.5rem' }}>
        <div style={{ flex: '1 1 30rem', minWidth: 0 }}>
          <Eyebrow>Contact</Eyebrow>
          <Display>Tell me about your project.</Display>
          <Lead style={{ maxWidth: '32.5rem', marginBottom: '2.5rem' }}>
            Send the main details of your project. I will reply with my view on the fit and a possible approach.
          </Lead>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '3.5rem' }}>
            <PrimaryButton as="a" href={DISCORD_DM} target="_blank" rel="noopener noreferrer">
              <DiscordIcon size={18} />
              Direct message on Discord
            </PrimaryButton>
            <GhostButton as="a" href={DISCORD_SERVER} target="_blank" rel="noopener noreferrer">
              Join the server
            </GhostButton>
          </div>
          <Facts>
            <div>
              <dt>DISCORD</dt>
              <dd>@skeered</dd>
            </div>
            <div>
              <dt>RESPONSE</dt>
              <dd>Usually &lt; 24 h</dd>
            </div>
            <div>
              <dt>TIME ZONE</dt>
              <dd>UTC+1 / CET</dd>
            </div>
          </Facts>
        </div>

        <Brief>
          <header>
            <span>project-brief.md</span>
            <span>what to send</span>
          </header>
          <ol>
            {BRIEF.map(([title, text], i) => (
              <li key={title}>
                <span>{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </Brief>
      </Container>
    </Section>

    <Section $band style={{ borderBottom: 0 }}>
      <Container>
        <Eyebrow>Fit</Eyebrow>
        <SectionTitle style={{ marginBottom: '3.5rem' }}>Projects that fit my work.</SectionTitle>
        <Grid $min="21.25rem">
          {FIT.map(([title, items]) => (
            <FitCard key={title}>
              <h3>{title}</h3>
              <CheckList>
                {items.map((item) => (
                  <li key={item}>
                    <Check accent />
                    {item}
                  </li>
                ))}
              </CheckList>
            </FitCard>
          ))}
        </Grid>
      </Container>
    </Section>
  </Page>
);

export default Contact;
